import React, { useState, useEffect, useCallback } from 'react';
import { getClients, syncExternalClients, getExternalDbInfo, getClientStats, getClientTriplets } from '../api';

const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const StatusBadge = ({ status }) => {
  const m = {
    pending: 'bg-yellow-500/10 text-yellow-400', scheduled: 'bg-blue-500/10 text-blue-400',
    processing: 'bg-purple-500/10 text-purple-400', completed: 'bg-green-500/10 text-green-400',
    declined: 'bg-red-500/10 text-red-400', no_gateway_available: 'bg-orange-500/10 text-orange-400',
    failed: 'bg-red-500/10 text-red-400',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${m[status] || 'bg-slate-500/10 text-slate-400'}`}>{status?.replace(/_/g, ' ')}</span>;
};

function ClientsTab({ socket, onSync }) {
  const [clients, setClients] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [totalInitial, setTotalInitial] = useState(0);
  const [totalRebill, setTotalRebill] = useState(0);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [dbInfo, setDbInfo] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('initial_transaction_date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [expandedClient, setExpandedClient] = useState(null);
  const [triplets, setTriplets] = useState([]);
  const PER_PAGE = 50;

  const fetchClients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('sort_by', sortBy); params.set('sort_dir', sortDir);
      params.set('limit', PER_PAGE); params.set('offset', (page - 1) * PER_PAGE);
      const res = await getClients(params.toString());
      if (res.success) {
        setClients(res.data.clients); setTotalClients(res.data.total);
        setTotalInitial(res.data.total_initial || 0); setTotalRebill(res.data.total_rebill || 0);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter, sortBy, sortDir, page]);

  const fetchStats = useCallback(async () => {
    try { const res = await getClientStats(); if (res.success) setStats(res.data); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchClients(); fetchStats(); }, [fetchClients, fetchStats]);
  useEffect(() => { const i = setInterval(() => { fetchClients(); fetchStats(); }, 15000); return () => clearInterval(i); }, [fetchClients, fetchStats]);
  useEffect(() => { setPage(1); }, [statusFilter, sortBy, sortDir]);

  useEffect(() => {
    if (!socket) return;
    const r = () => { fetchClients(); fetchStats(); };
    socket.on('job_completed', r); socket.on('job_declined', r); socket.on('sync_progress', r);
    return () => { socket.off('job_completed', r); socket.off('job_declined', r); socket.off('sync_progress', r); };
  }, [socket, fetchClients, fetchStats]);

  const handleSync = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const res = await syncExternalClients();
      if (res.success) { setSyncResult(res.data); fetchClients(); fetchStats(); if (onSync) onSync(); }
    } catch (e) { setSyncResult({ error: e.message }); }
    finally { setSyncing(false); }
  };

  const handleCheckDb = async () => {
    try { const res = await getExternalDbInfo(); if (res.success) setDbInfo(res.data); }
    catch (e) { setDbInfo({ connected: false, error: e.message }); }
  };

  const handleExpand = async (clientId) => {
    if (expandedClient === clientId) { setExpandedClient(null); setTriplets([]); return; }
    setExpandedClient(clientId);
    try { const res = await getClientTriplets(clientId); if (res.success) setTriplets(res.data); }
    catch (e) { setTriplets([]); }
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const totalPages = Math.ceil(totalClients / PER_PAGE);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-slate-500/10 rounded-xl p-3 border border-slate-700/50">
          <p className="text-[10px] text-slate-400 mb-0.5">Total Clients</p>
          <p className="text-xl font-bold text-white">{stats.total || 0}</p>
        </div>
        <div className="bg-yellow-500/10 rounded-xl p-3 border border-slate-700/50">
          <p className="text-[10px] text-slate-400 mb-0.5">Available to Rebill</p>
          <p className="text-xl font-bold text-yellow-400">{stats.pending || 0}</p>
        </div>
        <div className="bg-green-500/10 rounded-xl p-3 border border-slate-700/50">
          <p className="text-[10px] text-slate-400 mb-0.5">Approved</p>
          <p className="text-xl font-bold text-green-400">{stats.approved || 0}</p>
        </div>
        <div className="bg-red-500/10 rounded-xl p-3 border border-slate-700/50">
          <p className="text-[10px] text-slate-400 mb-0.5">Declined (Soft)</p>
          <p className="text-xl font-bold text-red-400">{stats.declined || 0}</p>
        </div>
        <div className="bg-blue-500/10 rounded-xl p-3 border border-slate-700/50">
          <p className="text-[10px] text-slate-400 mb-0.5">Scheduled</p>
          <p className="text-xl font-bold text-blue-400">{stats.scheduled || 0}</p>
        </div>
        <div className="bg-cyan-500/10 rounded-xl p-3 border border-slate-700/50">
          <p className="text-[10px] text-slate-400 mb-0.5">Total Rebill</p>
          <p className="text-xl font-bold text-cyan-400">{fmt(totalRebill)}</p>
        </div>
      </div>

      {/* Sync Controls */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-white">External Database Sync</h3>
            <button onClick={handleSync} disabled={syncing}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center space-x-2">
              {syncing ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /><span>Syncing...</span></> :
                <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg><span>Sync from External DB</span></>}
            </button>
            <button onClick={handleCheckDb} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors">🔌 Check DB</button>
          </div>
          <div className="flex space-x-1">
            {['', 'pending', 'scheduled', 'completed'].map(f => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${statusFilter === f ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'}`}>
                {f || 'All'}
              </button>
            ))}
          </div>
        </div>
        {syncResult && (
          <div className={`mt-4 p-3 rounded-lg text-sm ${syncResult.error ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-green-500/10 border border-green-500/30 text-green-400'}`}>
            {syncResult.error ? <p>❌ {syncResult.error}</p> : (
              <div>
                <p>✅ New: <strong>{syncResult.imported}</strong> imported | Total in DB: <strong>{syncResult.total_in_db || syncResult.imported}</strong> ({syncResult.approved || 0} approved, {syncResult.declined || 0} declined) | Enriched: <strong>{syncResult.enriched}</strong></p>
                {syncResult.cc_api_error && <p className="text-xs mt-1 text-red-400">⚠️ CheckoutChamp: {syncResult.cc_api_error}</p>}
              </div>
            )}
          </div>
        )}
        {dbInfo && (
          <div className={`mt-3 p-3 rounded-lg text-sm ${dbInfo.connected ? 'bg-green-500/10 border border-green-500/30 text-green-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>
            {dbInfo.connected ? <p>✅ {dbInfo.host}/{dbInfo.database} — {dbInfo.totalRows} total, {dbInfo.eligibleRows} eligible</p> : <p>❌ {dbInfo.error}</p>}
          </div>
        )}
      </div>

      {/* Client Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-700/50">
                <th className="px-2 py-3 w-8"></th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase cursor-pointer hover:text-white" onClick={() => toggleSort('initial_transaction_date')}>
                  Date {sortBy === 'initial_transaction_date' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Client</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Card</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-slate-400 uppercase cursor-pointer hover:text-white" onClick={() => toggleSort('amount')}>
                  Initial Amount {sortBy === 'amount' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-slate-400 uppercase cursor-pointer hover:text-white" onClick={() => toggleSort('rebill_total')}>
                  Rebill Total {sortBy === 'rebill_total' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {clients.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No clients found</td></tr>
              ) : clients.map(c => (
                <React.Fragment key={c.id}>
                  <tr className="hover:bg-slate-700/30">
                    <td className="px-2 py-2">
                      <button onClick={() => handleExpand(c.id)}
                        className="w-6 h-6 flex items-center justify-center rounded bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-white text-xs font-bold transition-colors">
                        {expandedClient === c.id ? '−' : '+'}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-slate-300 text-xs">{fmtDate(c.initial_transaction_date)}</td>
                    <td className="px-3 py-2">
                      <span className="text-white font-medium text-xs">{c.first_name} {c.last_name}</span><br/>
                      <span className="text-purple-400 font-mono text-xs">ID: {c.customer_id || c.id}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-400 font-mono text-xs">****{c.card_last4}</td>
                    <td className="px-3 py-2"><StatusBadge status={c.rebill_status} /></td>
                    <td className="px-3 py-2 text-right text-yellow-400 font-bold text-xs">{fmt(c.initial_amount)}</td>
                    <td className="px-3 py-2 text-right text-cyan-400 font-bold text-xs">{fmt(c.rebill_total)}</td>
                  </tr>
                  {expandedClient === c.id && (
                    <tr>
                      <td colSpan={7} className="px-4 py-3 bg-slate-700/30">
                        <div className="text-xs space-y-3">
                          {/* Client Details */}
                          <div>
                            <p className="text-slate-300 font-medium mb-2">Client Details</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">Name:</span> <span className="text-white">{c.first_name} {c.last_name}</span>
                              </div>
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">Customer ID:</span> <span className="text-purple-400">{c.customer_id || '—'}</span>
                              </div>
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">Address:</span> <span className="text-white">{c.address}{c.address2 ? `, ${c.address2}` : ''}</span>
                              </div>
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">City:</span> <span className="text-white">{c.city}, {c.state} {c.zip}</span>
                              </div>
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">Phone:</span> <span className="text-white">{c.phone_number || '—'}</span>
                              </div>
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">Email:</span> <span className="text-white">{c.email || '—'}</span>
                              </div>
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">Card BIN:</span> <span className="text-orange-400 font-mono">{c.card_bin || c.card_first6 || '—'}</span>
                              </div>
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">Card Type:</span> <span className="text-blue-400">{c.card_type || '—'}</span>
                              </div>
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">Card:</span> <span className="text-slate-300 font-mono">{c.card_first6 || '******'}****{c.card_last4}</span>
                              </div>
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">IP:</span> <span className="text-slate-300">{c.ip_address || '—'}</span>
                              </div>
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">Initial Amount:</span> <span className="text-yellow-400 font-bold">{fmt(c.initial_amount)}</span>
                              </div>
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">Expiry:</span> <span className="text-slate-300">{c.card_expiry || '—'}</span>
                              </div>
                              <div className="bg-slate-800/50 rounded p-2">
                                <span className="text-slate-500">Source:</span> <span className="text-slate-300">{c.source}</span>
                              </div>
                            </div>
                          </div>
                          {/* Triplets */}
                          <div>
                            <p className="text-slate-300 font-medium mb-2">Triplets (Processeur - Banque - DBA)</p>
                            {triplets.length === 0 ? (
                              <p className="text-slate-500 italic">Aucun triplet trouvé</p>
                            ) : triplets.map((t, i) => (
                              <div key={i} className="flex items-center justify-between py-1 px-2 bg-slate-800/50 rounded mb-1">
                                <div className="flex items-center space-x-3">
                                  <span className={`px-2 py-0.5 rounded text-xs ${t.source === 'checkoutchamp' ? 'bg-blue-500/10 text-blue-400' : 'bg-green-500/10 text-green-400'}`}>{t.source}</span>
                                  <span className="text-white font-medium">{t.processor} - {t.bank} - {t.dba}</span>
                                </div>
                                <span className="text-slate-500 text-xs">{t.transaction_date ? fmtDate(t.transaction_date) : (t.created_at ? fmtDate(t.created_at) : '—')}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
            <p className="text-xs text-slate-400">{(page-1)*PER_PAGE+1}–{Math.min(page*PER_PAGE, totalClients)} of {totalClients}</p>
            <div className="flex items-center space-x-2">
              <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page<=1} className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-white rounded text-xs">← Prev</button>
              <span className="text-xs text-slate-400">Page {page}/{totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page>=totalPages} className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-white rounded text-xs">Next →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientsTab;
